import axios, { type AxiosInstance, type AxiosResponse } from 'axios'
import { z } from 'zod'
import { ApiErrorSchema } from '@/utils/api-response.ts'

const DEFAULT_BASE_URL = '/'

export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL ?? DEFAULT_BASE_URL,
  timeout: 10000,
  // make the client not throw his exceptions
  validateStatus: () => true
})

/**
 * The company token provider function.
 * Returns a valid company-scoped JWT token with automatic refresh.
 */
let getCompanyToken: (() => Promise<string | null>) | null = null

/**
 * Sets the company token provider for authentication to the API backend.
 *
 * The company token is used for all business API calls (vehicles, users, sites, etc.).
 *
 * Note: Firebase token is NOT managed here - it's passed explicitly in headers
 * for the two endpoints that need it:
 * - GET /api/v1/companies (list companies)
 * - POST /api/v1/companies/{id}/token (exchange for company token)
 *
 * @param providers
 */
export const setTokenProviders = (providers: {
  getCompanyToken: (() => Promise<string | null>) | null
}) => {
  getCompanyToken = providers.getCompanyToken
}

apiClient.interceptors.request.use(
  async config => {
    // Only set Authorization header if none has been provided
    if (!config.headers.Authorization && getCompanyToken) {
      try {
        const token = await getCompanyToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      } catch (error) {
        console.error('Failed to get company token:', error)
      }
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

/**
 * Common wrapper for validating response codes and checking the response format
 * of an API call.
 *
 * @param apiCall callback to the actual axios call, such as apiClient.get
 * @param responseSchema the schema the response should adhere to, if successful
 *
 * @throws Error if any error occurs, or if the response code is not in [200, 300[.
 *
 * The error message is localised and must be translated with the 'api-errors' translation namespace
 */
export async function handleResponse<T extends z.ZodType>(
  apiCall: () => Promise<AxiosResponse<z.infer<T>>>,
  responseSchema: T
) {
  const response = await apiCall()
  if (response.status >= 200 && response.status <= 299) {
    const value = z.safeParse(responseSchema, response.data)
    if (value.success) {
      return value.data
    }
    throw new Error(
      `Could not parse response for ${responseSchema.type}: ${z.prettifyError(value.error)}`
    )
  }

  throw tryParseApiErrorResponse(response.data)
}

function tryParseApiErrorResponse(data: unknown): Error {
  const errorResponse = ApiErrorSchema.safeParse(data)

  if (!errorResponse.success) {
    throw Error('REQUEST_FAILED')
  }

  throw Error(
    errorResponse.data.messageCode ? errorResponse.data.messageCode : 'REQUEST_FAILED',
    { cause: errorResponse.data.message }
  )
}
